import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/errors";
import { roleLabel } from "@/context/AuthContext";
import { orgLookup } from "@/components/admin/ScopeFields";
import { useOrgs } from "@/context/OrgsContext";
import NewUserForm from "@/components/admin/NewUserForm";
import EditUserDialog from "@/components/admin/EditUserDialog";
import ResetPasswordDialog from "@/components/admin/ResetPasswordDialog";

const ASSIGNABLE = {
  super_admin: ["super_admin", "association_admin", "school_admin", "coach", "viewer"],
  association_admin: ["school_admin", "coach"],
  school_admin: ["coach"],
};

export default function UsersPanel({ users, reload, me, sports }) {
  const { orgs } = useOrgs();
  const l = orgLookup(orgs);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const roles = ASSIGNABLE[me.role] || [];

  const toggleForce = async (u, v) => {
    try {
      await api.patch(`/users/${u.id}`, { must_change_password: v });
      toast.success(v ? `${u.name} must change password at next login` : "Force password change turned off");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="users-panel">
      <NewUserForm roles={roles} sports={sports} onCreated={reload}/>
      <div className="border border-slate-300 bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-100"><TableRow>
            <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Association</TableHead><TableHead>School</TableHead>
            <TableHead className="whitespace-nowrap">Force PW Change</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></TableCell>
                <TableCell><Badge className="rounded-sm bg-slate-100 text-slate-700 border whitespace-nowrap">{roleLabel(u.role)}</Badge></TableCell>
                <TableCell className="text-xs" data-testid={`user-association-${u.id}`}>{l.association(u.association_id) || <span className="text-slate-400">{u.role === "super_admin" ? "All" : "—"}</span>}</TableCell>
                <TableCell className="text-xs" data-testid={`user-school-${u.id}`}>
                  {l.school(u.school_id) || <span className="text-slate-400">—</span>}
                  {u.role === "coach" && (u.sport_ids || []).length > 0 && (
                    <div className="text-[11px] text-slate-500">{u.sport_ids.map(id => sports.find(s => s.id === id)?.name).filter(Boolean).join(", ")}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Switch checked={!!u.must_change_password} disabled={u.id === me.id}
                    onCheckedChange={v => toggleForce(u, v)} data-testid={`force-change-${u.id}`}/>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-3 h-3"/></Button>
                    <Button variant="outline" size="sm" className="rounded-sm h-8 whitespace-nowrap" onClick={() => setResetting(u)} data-testid={`reset-password-${u.id}`}>
                      <KeyRound className="w-3 h-3 mr-1"/>Reset Password
                    </Button>
                    {u.id !== me.id && <Button variant="outline" size="sm" onClick={() => del(u)} className="rounded-sm h-8 text-red-600 border-red-300" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3 h-3"/></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {editing && <EditUserDialog target={editing} roles={roles.includes(editing.role) ? roles : [editing.role, ...roles]} sports={sports}
        isSelf={editing.id === me.id} onClose={() => setEditing(null)} onDone={reload}/>}
      {resetting && <ResetPasswordDialog target={resetting} onClose={() => setResetting(null)} onDone={reload}/>}
    </div>
  );
}
